import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Zap, Clock, Activity, Users, TrendingUp, Sparkles, Crown, Trophy, Star, Flame, Target, Award, Medal, Rocket, Swords, PartyPopper } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const WS_URL = API_URL?.replace('https://', 'wss://').replace('http://', 'ws://');

const DAILY_SALES_DURATION = 5 * 60 * 1000;
const LEADERBOARD_DURATION = 2 * 60 * 1000;

const COLORS = ['#6366f1', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#14b8a6'];

// Milestone thresholds for team celebrations
const MILESTONES = [5, 10, 15, 20, 25, 30, 40, 50, 75, 100];

// Achievement badge definitions
const ACHIEVEMENT_BADGES = {
  early_bird: { icon: '🌅', label: 'Early Bird', desc: 'First sale of the day' },
  hot_hand: { icon: '🔥', label: 'Hot Hand', desc: '3+ sales today' },
  closer_king: { icon: '👑', label: 'Top Closer', desc: 'Leading today' },
  comeback_kid: { icon: '⚡', label: 'Comeback', desc: '1 away from leader' },
  streak_master: { icon: '💎', label: 'On Fire', desc: '5+ sales today' },
};

// Play a simple bell/ting sound
const playBellSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 830;
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.4, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 1);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 1);
  } catch (e) {
    console.log('Audio not supported');
  }
};

// Play fanfare sound for milestones/leader takeover
const playFanfareSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const notes = [523, 659, 784, 1047]; // C5, E5, G5, C6
    
    notes.forEach((freq, i) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = freq;
      oscillator.type = 'sine';
      
      const startTime = audioContext.currentTime + i * 0.15;
      gainNode.gain.setValueAtTime(0.3, startTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + 0.4);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + 0.4);
    });
  } catch (e) {
    console.log('Audio not supported');
  }
};

const SALE_CELEBRATION_MESSAGES = [
  "BOOM! Another one closed!",
  "That's how it's done!",
  "Unstoppable!",
  "Money moves!",
  "Champion energy!",
  "On fire today!",
  "Crushing it!",
  "Legend in action!",
  "Deal hunter!",
  "Sales machine!",
];

const TVDisplayPage = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const initialView = urlParams.get('view') === 'leaderboard' ? 'leaderboard' : 'daily';
  
  const [currentView, setCurrentView] = useState(initialView);
  const [todaySales, setTodaySales] = useState([]);
  const [offers, setOffers] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState({ total_today: 0, month_period: '', working_days: 0, total_working_days: 0 });
  const [notifications, setNotifications] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [timeRemaining, setTimeRemaining] = useState(DAILY_SALES_DURATION);
  const [wsConnected, setWsConnected] = useState(false);
  const [isAutoRotate, setIsAutoRotate] = useState(true);
  const [tickerMessage, setTickerMessage] = useState({ text: "Let's make today count!", type: 'general' });
  
  // New states for gamification features
  const [milestoneAlert, setMilestoneAlert] = useState(null); // { milestone: 10, message: "10 SALES!" }
  const [leaderTakeover, setLeaderTakeover] = useState(null); // { name: "John", previousLeader: "Jane" }
  const [comebackAlert, setComebackAlert] = useState(null); // { name: "John", salesBehind: 1, leaderName: "Jane" }
  const [firstSaleAgent, setFirstSaleAgent] = useState(null); // Track who got first sale (Early Bird)
  const [tvBroadcast, setTvBroadcast] = useState(null); // Admin broadcast popup
  
  const notificationTimeoutRef = useRef(null);
  const rotationTimeoutRef = useRef(null);
  const countdownRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const messageRotationRef = useRef(null);
  const processedSaleIdsRef = useRef(new Set()); // De-dup for WebSocket messages
  const previousLeaderRef = useRef(null); // Track leader changes
  const previousTotalRef = useRef(0); // Track milestone crossings
  const milestoneTimeoutRef = useRef(null);
  const leaderTakeoverTimeoutRef = useRef(null);
  const comebackTimeoutRef = useRef(null);
  const tvBroadcastTimeoutRef = useRef(null);

  // Process sales data into matrix format (like portal's Performance Details)
  const performanceMatrix = useMemo(() => {
    const agentMap = {};
    const offerTotals = {};
    
    todaySales.forEach(sale => {
      const agentName = sale.agent_name || sale.user_name || 'Unknown';
      const offerName = sale.offer_name || 'Unknown';
      
      if (!agentMap[agentName]) {
        agentMap[agentName] = { name: agentName, offers: {}, total: 0 };
      }
      
      agentMap[agentName].offers[offerName] = (agentMap[agentName].offers[offerName] || 0) + 1;
      agentMap[agentName].total += 1;
      offerTotals[offerName] = (offerTotals[offerName] || 0) + 1;
    });
    
    // Sort agents by total sales
    const sortedAgents = Object.values(agentMap).sort((a, b) => b.total - a.total);
    
    return { agents: sortedAgents, offerTotals };
  }, [todaySales]);

  // Get today's top performer
  const topPerformer = useMemo(() => {
    return performanceMatrix.agents.length > 0 
      ? { name: performanceMatrix.agents[0].name, count: performanceMatrix.agents[0].total } 
      : null;
  }, [performanceMatrix]);

  // Compute achievement badges for each agent
  const agentBadges = useMemo(() => {
    const badges = {};
    const agents = performanceMatrix.agents;
    
    if (agents.length === 0) return badges;
    
    agents.forEach((agent, index) => {
      badges[agent.name] = [];
      
      // Top Closer badge (leading today)
      if (index === 0 && agent.total > 0) {
        badges[agent.name].push('closer_king');
      }
      
      // Hot Hand badge (3+ sales)
      if (agent.total >= 3) {
        badges[agent.name].push('hot_hand');
      }
      
      // Streak Master badge (5+ sales)
      if (agent.total >= 5) {
        badges[agent.name].push('streak_master');
      }
      
      // Comeback Kid badge (1 sale away from leader, not the leader)
      if (index > 0 && agents[0].total - agent.total === 1) {
        badges[agent.name].push('comeback_kid');
      }
    });
    
    // Early Bird badge (first sale of the day)
    if (firstSaleAgent && badges[firstSaleAgent]) {
      badges[firstSaleAgent].push('early_bird');
    }
    
    return badges;
  }, [performanceMatrix, firstSaleAgent]);

  // Check for comeback situations (agents 1-2 sales behind leader)
  const comebackSituations = useMemo(() => {
    const agents = performanceMatrix.agents;
    if (agents.length < 2) return [];
    
    const leader = agents[0];
    return agents.slice(1).filter(agent => {
      const gap = leader.total - agent.total;
      return gap > 0 && gap <= 2;
    }).map(agent => ({
      name: agent.name,
      salesBehind: leader.total - agent.total,
      leaderName: leader.name
    }));
  }, [performanceMatrix]);

  // Generate personalized ticker messages
  const generateTickerMessage = useCallback(() => {
    const totalSales = stats.total_today;
    const agents = performanceMatrix.agents;
    const messages = [];
    
    if (totalSales === 0) {
      messages.push(
        { text: "The floor is open! Who's closing first?", type: 'general' },
        { text: "Fresh day, fresh opportunities!", type: 'general' },
        { text: "Champions are made in moments like this!", type: 'general' },
        { text: "Ready to dominate? Let's go!", type: 'general' },
      );
    } else {
      if (agents.length > 0) {
        const firstName = agents[0].name.split(' ')[0];
        const topCount = agents[0].total;
        
        messages.push(
          { text: `${firstName} is leading with ${topCount} sales! Who's catching up?`, type: 'leader' },
          { text: `${firstName} is on FIRE today!`, type: 'leader' },
          { text: `Can anyone beat ${firstName}'s ${topCount} sales?`, type: 'challenge' },
        );
        
        if (topCount >= 3) messages.push({ text: `${firstName} is CRUSHING IT with ${topCount} closes!`, type: 'leader' });
        if (topCount >= 5) messages.push({ text: `UNSTOPPABLE! ${firstName} has ${topCount} sales!`, type: 'leader' });
      }
      
      if (agents.length > 1) {
        const gap = agents[0].total - agents[1].total;
        const secondName = agents[1].name.split(' ')[0];
        if (gap <= 2 && gap > 0) {
          messages.push({ text: `${secondName} is just ${gap} sale${gap > 1 ? 's' : ''} behind! The race is ON!`, type: 'race' });
        }
      }
      
      agents.slice(0, 5).forEach(agent => {
        const firstName = agent.name.split(' ')[0];
        if (agent.total >= 2) messages.push({ text: `${firstName} has ${agent.total} sales today!`, type: 'performer' });
      });
      
      messages.push(
        { text: `${totalSales} sales and counting! Keep the momentum!`, type: 'general' },
        { text: "Every call is an opportunity!", type: 'general' },
        { text: "The energy is ELECTRIC today!", type: 'general' },
        { text: "This team is BUILT DIFFERENT!", type: 'general' },
      );
      
      if (totalSales >= 5) messages.push({ text: "We're heating up! Don't stop now!", type: 'milestone' });
      if (totalSales >= 10) messages.push({ text: "DOUBLE DIGITS! UNSTOPPABLE!", type: 'milestone' });
      if (totalSales >= 15) messages.push({ text: "INCREDIBLE pace! Records are meant to be BROKEN!", type: 'milestone' });
      if (totalSales >= 20) messages.push({ text: "LEGENDARY performance! History in the making!", type: 'milestone' });
    }
    
    setTickerMessage(messages[Math.floor(Math.random() * messages.length)]);
  }, [stats.total_today, performanceMatrix.agents]);

  // Detect milestone crossings (5, 10, 15, etc.)
  useEffect(() => {
    const currentTotal = stats.total_today;
    const prevTotal = previousTotalRef.current;
    
    if (currentTotal > prevTotal) {
      // Check if we crossed a milestone
      const crossedMilestone = MILESTONES.find(m => prevTotal < m && currentTotal >= m);
      
      if (crossedMilestone) {
        // Trigger milestone celebration
        const messages = [
          `🎉 ${crossedMilestone} SALES! INCREDIBLE!`,
          `🚀 MILESTONE HIT: ${crossedMilestone} DEALS!`,
          `💥 ${crossedMilestone} AND COUNTING!`,
          `🔥 TEAM ON FIRE: ${crossedMilestone} SALES!`,
        ];
        setMilestoneAlert({
          milestone: crossedMilestone,
          message: messages[Math.floor(Math.random() * messages.length)]
        });
        playFanfareSound();
        
        // Clear after 5 seconds
        if (milestoneTimeoutRef.current) clearTimeout(milestoneTimeoutRef.current);
        milestoneTimeoutRef.current = setTimeout(() => setMilestoneAlert(null), 5000);
      }
    }
    
    previousTotalRef.current = currentTotal;
  }, [stats.total_today]);

  // Detect leader changes (New Leader Takeover)
  useEffect(() => {
    const agents = performanceMatrix.agents;
    if (agents.length === 0) return;
    
    const currentLeader = agents[0]?.name;
    const prevLeader = previousLeaderRef.current;
    
    // Only trigger if there was a previous leader and it changed
    if (prevLeader && currentLeader && prevLeader !== currentLeader && agents[0].total > 0) {
      setLeaderTakeover({
        name: currentLeader,
        previousLeader: prevLeader,
        salesCount: agents[0].total
      });
      playFanfareSound();
      
      // Clear after 6 seconds
      if (leaderTakeoverTimeoutRef.current) clearTimeout(leaderTakeoverTimeoutRef.current);
      leaderTakeoverTimeoutRef.current = setTimeout(() => setLeaderTakeover(null), 6000);
    }
    
    previousLeaderRef.current = currentLeader;
  }, [performanceMatrix.agents]);

  // Track first sale of the day (Early Bird)
  useEffect(() => {
    if (todaySales.length === 1 && !firstSaleAgent) {
      const firstSale = todaySales[0];
      setFirstSaleAgent(firstSale.agent_name || firstSale.user_name);
    }
  }, [todaySales, firstSaleAgent]);

  // DISABLED: Comeback alerts - only show popups for Sales & Broadcasts now
  // useEffect(() => {
  //   if (comebackSituations.length > 0) {
  //     const showComebackAlert = () => {
  //       const situation = comebackSituations[Math.floor(Math.random() * comebackSituations.length)];
  //       setComebackAlert(situation);
  //       if (comebackTimeoutRef.current) clearTimeout(comebackTimeoutRef.current);
  //       comebackTimeoutRef.current = setTimeout(() => setComebackAlert(null), 4000);
  //     };
  //     showComebackAlert();
  //     const interval = setInterval(showComebackAlert, 45000);
  //     return () => clearInterval(interval);
  //   }
  // }, [comebackSituations]);

  useEffect(() => {
    generateTickerMessage();
    messageRotationRef.current = setInterval(generateTickerMessage, 6000);
    return () => { if (messageRotationRef.current) clearInterval(messageRotationRef.current); };
  }, [generateTickerMessage]);

  const timeZones = useMemo(() => ({
    ist: currentTime.toLocaleTimeString('en-US', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }),
    est: currentTime.toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }),
  }), [currentTime]);

  const shiftInfo = useMemo(() => {
    const istTime = new Date(currentTime.toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }));
    const hour = istTime.getHours();
    const minute = istTime.getMinutes();
    const totalMins = hour * 60 + minute;
    const shiftStart = 19 * 60;
    const shiftEnd = 4 * 60 + 30;
    const totalShift = (24 * 60 - shiftStart) + shiftEnd;
    let isActive = totalMins >= shiftStart || totalMins < shiftEnd;
    let elapsed = isActive ? (totalMins >= shiftStart ? totalMins - shiftStart : (24 * 60 - shiftStart) + totalMins) : 0;
    const remaining = isActive ? totalShift - elapsed : shiftStart - totalMins;
    return { isActive, remaining: `${Math.floor(remaining / 60)}h ${remaining % 60}m` };
  }, [currentTime]);

  // WebSocket connection
  const connectWebSocket = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    
    console.log('Connecting to WebSocket:', `${WS_URL}/api/ws/tv-display`);
    
    try {
      wsRef.current = new WebSocket(`${WS_URL}/api/ws/tv-display`);
      
      wsRef.current.onopen = () => {
        console.log('WebSocket connected successfully');
        setWsConnected(true);
        const ping = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
        wsRef.current.pingInterval = ping;
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_sale') {
            const saleId = data.sale?.id;
            // De-duplicate: skip if we've already processed this sale
            if (saleId && processedSaleIdsRef.current.has(saleId)) {
              console.log('Duplicate sale ignored:', saleId);
              return;
            }
            if (saleId) {
              processedSaleIdsRef.current.add(saleId);
              // Clean up old IDs after 10 seconds to prevent memory growth
              setTimeout(() => processedSaleIdsRef.current.delete(saleId), 10000);
            }
            console.log('New sale received:', data.sale);
            setTodaySales(prev => [data.sale, ...prev]);
            setStats(prev => ({ ...prev, total_today: prev.total_today + 1 }));
            triggerNotification(data.sale);
          } else if (data.type === 'sale_deleted') {
            const deleteId = data.sale_id;
            // De-duplicate deletions
            const deleteKey = `del_${deleteId}`;
            if (processedSaleIdsRef.current.has(deleteKey)) {
              console.log('Duplicate deletion ignored:', deleteId);
              return;
            }
            processedSaleIdsRef.current.add(deleteKey);
            setTimeout(() => processedSaleIdsRef.current.delete(deleteKey), 10000);
            
            setTodaySales(prev => prev.filter(s => s.id !== deleteId));
            setStats(prev => ({ ...prev, total_today: Math.max(0, prev.total_today - 1) }));
          } else if (data.type === 'sale_updated') {
            setTodaySales(prev => prev.map(s => s.id === data.sale.id ? data.sale : s));
          } else if (data.type === 'tv_broadcast') {
            // Admin broadcast - show for specified duration (default 60 seconds)
            console.log('TV Broadcast received:', data);
            setTvBroadcast({
              title: data.title,
              message: data.message,
              from: data.from,
              priority: data.priority
            });
            playFanfareSound();
            
            // Auto-close after duration (default 60 seconds)
            const duration = (data.duration || 60) * 1000;
            if (tvBroadcastTimeoutRef.current) clearTimeout(tvBroadcastTimeoutRef.current);
            tvBroadcastTimeoutRef.current = setTimeout(() => setTvBroadcast(null), duration);
          }
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };
      
      wsRef.current.onclose = (event) => {
        console.log('WebSocket closed:', event.code, event.reason);
        setWsConnected(false);
        if (wsRef.current?.pingInterval) clearInterval(wsRef.current.pingInterval);
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, 2000);
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (e) {
      console.error('WebSocket connection failed:', e);
      reconnectTimeoutRef.current = setTimeout(connectWebSocket, 2000);
    }
  }, []);

  const fetchTodaySales = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tv/today-sales`);
      const sales = res.data.sales || [];
      setTodaySales(sales);
      setOffers(res.data.offers || []);
      setStats(prev => ({ ...prev, total_today: sales.length }));
    } catch (error) {
      console.error('Error fetching sales:', error);
    }
  }, []);

  const fetchLeaderboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tv/leaderboard`);
      if (res.data.leaderboard) {
        setLeaderboard(res.data.leaderboard);
        setStats(prev => ({ 
          ...prev, 
          month_period: res.data.month_period, 
          working_days: res.data.working_days_elapsed, 
          total_working_days: res.data.total_working_days 
        }));
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  }, []);

  const triggerNotification = (sale) => {
    const agentName = sale.agent_name || sale.user_name || 'Someone';
    const celebrationMsg = SALE_CELEBRATION_MESSAGES[Math.floor(Math.random() * SALE_CELEBRATION_MESSAGES.length)];
    
    console.log('Triggering notification for:', agentName);
    
    setNotifications([{ 
      id: Date.now(), 
      user_name: agentName, 
      offer_name: sale.offer_name,
      celebration: celebrationMsg
    }]);
    
    playBellSound();
    
    if (notificationTimeoutRef.current) clearTimeout(notificationTimeoutRef.current);
    notificationTimeoutRef.current = setTimeout(() => setNotifications([]), 6000);
  };

  const switchView = (view) => {
    setCurrentView(view);
    setTimeRemaining(view === 'daily' ? DAILY_SALES_DURATION : LEADERBOARD_DURATION);
  };

  useEffect(() => {
    connectWebSocket();
    return () => {
      if (wsRef.current) { 
        if (wsRef.current.pingInterval) clearInterval(wsRef.current.pingInterval); 
        wsRef.current.close(); 
      }
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
    };
  }, [connectWebSocket]);

  useEffect(() => {
    if (!isAutoRotate) return;
    const duration = currentView === 'daily' ? DAILY_SALES_DURATION : LEADERBOARD_DURATION;
    setTimeRemaining(duration);
    if (rotationTimeoutRef.current) clearTimeout(rotationTimeoutRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => setTimeRemaining(prev => Math.max(0, prev - 1000)), 1000);
    rotationTimeoutRef.current = setTimeout(() => setCurrentView(prev => prev === 'daily' ? 'leaderboard' : 'daily'), duration);
    return () => { 
      if (rotationTimeoutRef.current) clearTimeout(rotationTimeoutRef.current); 
      if (countdownRef.current) clearInterval(countdownRef.current); 
    };
  }, [currentView, isAutoRotate]);

  useEffect(() => { 
    const timer = setInterval(() => setCurrentTime(new Date()), 1000); 
    return () => clearInterval(timer); 
  }, []);

  useEffect(() => {
    fetchTodaySales(); 
    fetchLeaderboard();
    const interval = setInterval(() => { 
      fetchTodaySales(); 
      fetchLeaderboard(); 
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchTodaySales, fetchLeaderboard]);

  const formatTime = (ms) => `${Math.floor(ms / 60000)}:${String(Math.floor((ms % 60000) / 1000)).padStart(2, '0')}`;

  // ==================== HEADER ====================
  const Header = () => (
    <header className="h-20 lg:h-24 border-b border-slate-200 bg-white flex items-center justify-between px-4 lg:px-8 xl:px-12">
      <div className="flex items-center gap-4 lg:gap-8 xl:gap-12">
        <div className="flex items-center gap-3 lg:gap-4">
          <div className="w-10 h-10 lg:w-14 lg:h-14 rounded-xl lg:rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center shadow-lg shadow-indigo-200">
            <TrendingUp className="w-5 h-5 lg:w-7 lg:h-7 text-white" />
          </div>
          <div>
            <h1 className="text-xl lg:text-2xl xl:text-3xl font-black text-slate-900 tracking-tight">OMNILINK</h1>
            <p className="text-xs lg:text-sm text-slate-400 font-semibold tracking-[0.15em] lg:tracking-[0.2em]">PERFORMANCE HUB</p>
          </div>
        </div>
        
        <div className="h-10 lg:h-12 w-px bg-slate-200 hidden md:block" />
        
        <div className="flex items-center bg-slate-100 rounded-xl lg:rounded-2xl p-1 lg:p-2">
          <button onClick={() => switchView('daily')} className={`px-4 lg:px-6 xl:px-8 py-2 lg:py-3 rounded-lg lg:rounded-xl text-sm lg:text-base font-bold transition-all ${currentView === 'daily' ? 'bg-white text-slate-900 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}>
            Daily Sales
          </button>
          <button onClick={() => switchView('leaderboard')} className={`px-4 lg:px-6 xl:px-8 py-2 lg:py-3 rounded-lg lg:rounded-xl text-sm lg:text-base font-bold transition-all ${currentView === 'leaderboard' ? 'bg-white text-slate-900 shadow-md' : 'text-slate-500 hover:text-slate-700'}`}>
            Leaderboard
          </button>
        </div>
        
        <button onClick={() => setIsAutoRotate(!isAutoRotate)} className={`px-3 lg:px-5 py-2 lg:py-2.5 rounded-lg lg:rounded-xl text-sm lg:text-base font-bold transition-all hidden sm:block ${isAutoRotate ? 'bg-emerald-100 text-emerald-700 border-2 border-emerald-300' : 'bg-slate-100 text-slate-500 border-2 border-slate-200'}`}>
          Auto {isAutoRotate ? 'ON' : 'OFF'}
        </button>
      </div>
      
      <div className="flex items-center gap-4 lg:gap-8 xl:gap-12">
        <div className={`flex items-center gap-2 lg:gap-3 px-3 lg:px-5 py-2 lg:py-3 rounded-xl lg:rounded-2xl hidden md:flex ${shiftInfo.isActive ? 'bg-emerald-50 border-2 border-emerald-200' : 'bg-slate-50 border-2 border-slate-200'}`}>
          <div className={`w-3 h-3 rounded-full ${shiftInfo.isActive ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`} />
          <span className={`text-sm lg:text-base font-bold ${shiftInfo.isActive ? 'text-emerald-700' : 'text-slate-500'}`}>
            {shiftInfo.isActive ? 'Shift Active' : 'Starts in'} 
          </span>
          <span className={`text-lg lg:text-xl font-black ${shiftInfo.isActive ? 'text-emerald-600' : 'text-slate-600'}`}>{shiftInfo.remaining}</span>
        </div>
        
        <div className="flex items-center gap-4 lg:gap-6 xl:gap-10">
          <div className="text-right hidden lg:block">
            <p className="text-xs font-bold text-slate-400 tracking-widest">INDIA</p>
            <p className="text-2xl xl:text-3xl font-black text-slate-900 tabular-nums tracking-tight">{timeZones.ist}</p>
          </div>
          <div className="h-8 lg:h-10 w-px bg-slate-200 hidden lg:block" />
          <div className="text-right">
            <p className="text-xs font-bold text-slate-400 tracking-widest">NEW YORK</p>
            <p className="text-2xl xl:text-3xl font-black text-indigo-600 tabular-nums tracking-tight">{timeZones.est}</p>
          </div>
        </div>
        
        <div className={`w-3 h-3 lg:w-4 lg:h-4 rounded-full ${wsConnected ? 'bg-emerald-500' : 'bg-red-500 animate-pulse'}`} title={wsConnected ? 'Live Connected' : 'Reconnecting...'} />
      </div>
    </header>
  );

  // ==================== TICKER BAR ====================
  const TickerBar = () => (
    <div className="flex">
      <div className={`flex-1 py-2.5 lg:py-3 px-4 lg:px-8 ${
        tickerMessage.type === 'leader' ? 'bg-gradient-to-r from-amber-500 to-orange-500' :
        tickerMessage.type === 'milestone' ? 'bg-gradient-to-r from-emerald-500 to-teal-500' :
        tickerMessage.type === 'race' ? 'bg-gradient-to-r from-rose-500 to-pink-500' :
        tickerMessage.type === 'challenge' ? 'bg-gradient-to-r from-purple-500 to-violet-500' :
        'bg-gradient-to-r from-indigo-600 to-violet-600'
      }`}>
        <div className="flex items-center justify-center gap-2 lg:gap-4">
          {tickerMessage.type === 'leader' ? <Flame className="w-4 h-4 lg:w-5 lg:h-5 text-white animate-pulse" /> :
           tickerMessage.type === 'milestone' ? <Star className="w-4 h-4 lg:w-5 lg:h-5 text-white animate-pulse" /> :
           tickerMessage.type === 'race' ? <Target className="w-4 h-4 lg:w-5 lg:h-5 text-white animate-pulse" /> :
           <Sparkles className="w-4 h-4 lg:w-5 lg:h-5 text-yellow-300 animate-pulse" />}
          <p className="text-sm lg:text-base xl:text-lg font-bold text-white tracking-wide">{tickerMessage.text}</p>
          {tickerMessage.type === 'leader' ? <Flame className="w-4 h-4 lg:w-5 lg:h-5 text-white animate-pulse" /> :
           tickerMessage.type === 'milestone' ? <Star className="w-4 h-4 lg:w-5 lg:h-5 text-white animate-pulse" /> :
           tickerMessage.type === 'race' ? <Target className="w-4 h-4 lg:w-5 lg:h-5 text-white animate-pulse" /> :
           <Sparkles className="w-4 h-4 lg:w-5 lg:h-5 text-yellow-300 animate-pulse" />}
        </div>
      </div>
      
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 py-2.5 lg:py-3 px-4 lg:px-8 flex items-center gap-2 lg:gap-3 min-w-[200px] lg:min-w-[280px]">
        <Crown className="w-4 h-4 lg:w-5 lg:h-5 text-white" />
        <div className="text-white">
          <p className="text-xs lg:text-sm font-semibold text-amber-100">Today&apos;s Leader</p>
          <p className="text-base lg:text-lg font-black">
            {topPerformer ? `${topPerformer.name} (${topPerformer.count})` : 'Waiting for first sale'}
          </p>
        </div>
        <Trophy className="w-4 h-4 lg:w-5 lg:h-5 text-white hidden sm:block" />
      </div>
    </div>
  );

  // ==================== DAILY SALES VIEW - PERFORMANCE DETAILS TABLE ====================
  const DailySalesView = () => (
    <div className="flex-1 flex flex-col overflow-hidden">
      <TickerBar />
      
      <div className="flex-1 p-4 lg:p-6 overflow-hidden flex flex-col">
        <div className="flex items-center justify-between mb-4 lg:mb-6">
          <div>
            <p className="text-sm lg:text-base font-bold text-indigo-600 tracking-widest uppercase mb-0.5">Live From The Floor</p>
            <h2 className="text-3xl lg:text-4xl xl:text-5xl font-black text-slate-900 tracking-tight">Battle Zone</h2>
            <p className="text-base lg:text-lg text-slate-500 mt-1">Every deal counts • Resets at 5:00 AM EST</p>
          </div>
          <div className="flex items-center gap-2 lg:gap-4 bg-gradient-to-r from-indigo-600 to-violet-600 text-white px-4 lg:px-8 xl:px-10 py-3 lg:py-5 xl:py-6 rounded-2xl lg:rounded-3xl shadow-xl shadow-indigo-200">
            <Zap className="w-8 h-8 lg:w-10 lg:h-10 xl:w-12 xl:h-12 text-yellow-300" />
            <div>
              <p className="text-sm lg:text-base font-semibold text-indigo-200">Total Sales</p>
              <p className="text-4xl lg:text-5xl xl:text-7xl font-black tabular-nums leading-none">{stats.total_today}</p>
            </div>
          </div>
        </div>

        {/* Performance Details Table - Matrix Format */}
        <div className="flex-1 bg-white rounded-2xl lg:rounded-3xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
            <div className="flex items-center gap-2 lg:gap-4">
              <Activity className="w-5 h-5 lg:w-6 lg:h-6 text-indigo-600" />
              <div>
                <h3 className="text-lg lg:text-xl font-bold text-slate-900">Performance Details</h3>
                <p className="text-xs lg:text-sm text-slate-500">{performanceMatrix.agents.length} agents • {offers.length} products</p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-3 lg:px-4 py-1.5 lg:py-2 bg-slate-100 rounded-lg lg:rounded-xl">
              <div className={`w-2.5 h-2.5 lg:w-3 lg:h-3 rounded-full ${wsConnected ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
              <span className="text-sm lg:text-base font-semibold text-slate-600">{wsConnected ? 'Live' : 'Reconnecting'}</span>
            </div>
          </div>
          
          <div className="flex-1 overflow-auto">
            {performanceMatrix.agents.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-slate-400">
                <Zap className="w-20 h-20 lg:w-28 lg:h-28 text-slate-200 mb-4 lg:mb-6" />
                <p className="text-xl lg:text-3xl font-bold">Waiting for sales</p>
                <p className="text-base lg:text-xl mt-2">Performance will appear here in real-time</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 lg:px-5 py-2.5 lg:py-3 text-xs lg:text-sm font-bold text-slate-600 uppercase tracking-wider min-w-[140px] lg:min-w-[180px]">Agent</th>
                    {offers.map((offer, i) => (
                      <th key={offer} className="text-center px-2 lg:px-3 py-2.5 lg:py-3 text-[10px] lg:text-xs font-bold text-slate-600 uppercase tracking-wider min-w-[60px] lg:min-w-[80px]">
                        {offer}
                      </th>
                    ))}
                    <th className="text-center px-3 lg:px-5 py-2.5 lg:py-3 text-xs lg:text-sm font-bold text-slate-600 uppercase tracking-wider bg-slate-100 min-w-[70px] lg:min-w-[90px]">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {performanceMatrix.agents.map((agent, agentIndex) => (
                    <tr 
                      key={agent.name}
                      className={`transition-colors ${
                        agentIndex === 0 ? 'bg-gradient-to-r from-amber-50 to-yellow-50' :
                        agentIndex === 1 ? 'bg-gradient-to-r from-slate-50 to-gray-50' :
                        agentIndex === 2 ? 'bg-gradient-to-r from-orange-50 to-amber-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <td className="px-3 lg:px-5 py-2 lg:py-3">
                        <div className="flex items-center gap-2 lg:gap-3">
                          <div className={`w-8 h-8 lg:w-10 lg:h-10 rounded-lg lg:rounded-xl flex items-center justify-center text-sm lg:text-base font-bold ${
                            agentIndex === 0 ? 'bg-gradient-to-br from-amber-400 to-yellow-500 text-white shadow-md' :
                            agentIndex === 1 ? 'bg-gradient-to-br from-slate-400 to-gray-500 text-white' :
                            agentIndex === 2 ? 'bg-gradient-to-br from-orange-400 to-amber-500 text-white' :
                            'bg-slate-200 text-slate-600'
                          }`}>
                            {agentIndex < 3 ? (
                              agentIndex === 0 ? <Crown className="w-4 h-4 lg:w-5 lg:h-5" /> :
                              agentIndex === 1 ? <Medal className="w-4 h-4 lg:w-5 lg:h-5" /> :
                              <Award className="w-4 h-4 lg:w-5 lg:h-5" />
                            ) : (
                              agentIndex + 1
                            )}
                          </div>
                          <div>
                            <p className={`text-sm lg:text-base font-bold ${
                              agentIndex === 0 ? 'text-amber-700' :
                              agentIndex === 1 ? 'text-slate-700' :
                              agentIndex === 2 ? 'text-orange-700' :
                              'text-slate-900'
                            }`}>
                              {agent.name}
                            </p>
                            <AgentBadges agentName={agent.name} />
                          </div>
                        </div>
                      </td>
                      {offers.map((offer, i) => {
                        const count = agent.offers[offer] || 0;
                        return (
                          <td key={offer} className="text-center px-2 lg:px-3 py-2 lg:py-3">
                            {count > 0 ? (
                              <span 
                                className="inline-flex items-center justify-center min-w-[28px] lg:min-w-[36px] h-7 lg:h-9 px-2 lg:px-3 rounded-full text-white font-bold text-sm lg:text-base shadow-sm"
                                style={{ backgroundColor: COLORS[i % COLORS.length] }}
                              >
                                {count}
                              </span>
                            ) : (
                              <span className="text-slate-300 text-lg lg:text-xl">-</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="text-center px-3 lg:px-5 py-2 lg:py-3 bg-slate-50/50">
                        <span className={`inline-flex items-center justify-center min-w-[36px] lg:min-w-[44px] h-8 lg:h-10 px-3 lg:px-4 rounded-full text-white font-black text-base lg:text-lg shadow-md ${
                          agentIndex === 0 ? 'bg-gradient-to-r from-amber-500 to-yellow-500' :
                          agentIndex === 1 ? 'bg-gradient-to-r from-slate-500 to-gray-500' :
                          agentIndex === 2 ? 'bg-gradient-to-r from-orange-500 to-amber-500' :
                          'bg-gradient-to-r from-indigo-500 to-purple-500'
                        }`}>
                          {agent.total}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {/* Team Total Row */}
                  <tr className="bg-gradient-to-r from-slate-100 to-slate-200 font-bold">
                    <td className="px-3 lg:px-5 py-2 lg:py-3">
                      <div className="flex items-center gap-2 lg:gap-3">
                        <div className="w-8 h-8 lg:w-10 lg:h-10 rounded-lg lg:rounded-xl bg-indigo-600 flex items-center justify-center">
                          <Trophy className="w-4 h-4 lg:w-5 lg:h-5 text-white" />
                        </div>
                        <span className="text-sm lg:text-base font-black text-slate-900">Team Total</span>
                      </div>
                    </td>
                    {offers.map((offer) => {
                      const total = performanceMatrix.offerTotals[offer] || 0;
                      return (
                        <td key={offer} className="text-center px-2 lg:px-3 py-2 lg:py-3">
                          <span className={`font-black text-base lg:text-lg ${total > 0 ? 'text-slate-700' : 'text-slate-400'}`}>
                            {total}
                          </span>
                        </td>
                      );
                    })}
                    <td className="text-center px-3 lg:px-5 py-2 lg:py-3 bg-gradient-to-r from-indigo-200 to-purple-200">
                      <span className="text-indigo-800 font-black text-xl lg:text-2xl">{stats.total_today}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  // ==================== LEADERBOARD VIEW ====================
  const LeaderboardView = () => (
    <div className="flex-1 p-4 lg:p-6 overflow-hidden flex flex-col">
      <div className="mb-3 lg:mb-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm lg:text-base font-bold text-amber-600 tracking-widest uppercase mb-0.5">Who's Dominating?</p>
            <h2 className="text-3xl lg:text-4xl font-black text-slate-900 tracking-tight">Champions Arena</h2>
            <p className="text-base lg:text-lg text-slate-500 mt-1">{stats.month_period} • {stats.working_days} of {stats.total_working_days} working days</p>
          </div>
          <div className="flex items-center gap-2 bg-slate-100 px-4 lg:px-6 py-2 lg:py-3 rounded-xl lg:rounded-2xl">
            <Users className="w-5 h-5 lg:w-6 lg:h-6 text-slate-500" />
            <span className="text-lg lg:text-xl font-bold text-slate-700">{leaderboard.length} Team Members</span>
          </div>
        </div>
      </div>

      <div className="flex-1 bg-white rounded-2xl lg:rounded-3xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-auto h-full">
          <table className="w-full">
            <thead className="bg-slate-50 sticky top-0">
              <tr>
                <th className="text-left px-4 lg:px-6 py-3 text-xs lg:text-sm font-bold text-slate-600 uppercase tracking-wider w-16 lg:w-20">Rank</th>
                <th className="text-left px-4 lg:px-6 py-3 text-xs lg:text-sm font-bold text-slate-600 uppercase tracking-wider">Agent Name</th>
                <th className="text-center px-4 lg:px-6 py-3 text-xs lg:text-sm font-bold text-slate-600 uppercase tracking-wider w-28 lg:w-36">Total Sales</th>
                <th className="text-center px-4 lg:px-6 py-3 text-xs lg:text-sm font-bold text-slate-600 uppercase tracking-wider w-28 lg:w-40">Daily Average</th>
                <th className="text-left px-4 lg:px-6 py-3 text-xs lg:text-sm font-bold text-slate-600 uppercase tracking-wider w-48 lg:w-60">Performance</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {leaderboard.map((user, i) => {
                const maxSales = leaderboard[0]?.sales_count || 1;
                const percentage = maxSales > 0 ? (user.sales_count / maxSales) * 100 : 0;
                
                return (
                  <tr key={user.user_id} className={`transition-colors ${i === 0 ? 'bg-amber-50' : i < 3 ? 'bg-slate-50/50' : 'hover:bg-slate-50'}`}>
                    <td className="px-4 lg:px-6 py-2 lg:py-3">
                      <div className={`w-9 h-9 lg:w-10 lg:h-10 rounded-lg lg:rounded-xl flex items-center justify-center text-sm lg:text-base font-black ${
                        i === 0 ? 'bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-md shadow-amber-200' 
                        : i === 1 ? 'bg-slate-300 text-slate-700' 
                        : i === 2 ? 'bg-amber-200 text-amber-800' 
                        : 'bg-slate-100 text-slate-500'
                      }`}>
                        {i + 1}
                      </div>
                    </td>
                    <td className="px-4 lg:px-6 py-2 lg:py-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-9 h-9 lg:w-10 lg:h-10 rounded-lg lg:rounded-xl flex items-center justify-center text-sm lg:text-base font-bold ${i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                          {user.name.charAt(0)}
                        </div>
                        <div>
                          <p className={`text-base lg:text-lg font-bold ${i === 0 ? 'text-amber-900' : 'text-slate-900'}`}>{user.name}</p>
                          {i === 0 && <p className="text-xs lg:text-sm text-amber-600 font-semibold">Top Performer</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 lg:px-6 py-2 lg:py-3 text-center">
                      <span className={`text-2xl lg:text-3xl font-black tabular-nums ${i === 0 ? 'text-amber-600' : i < 3 ? 'text-slate-800' : 'text-slate-500'}`}>
                        {user.sales_count}
                      </span>
                    </td>
                    <td className="px-4 lg:px-6 py-2 lg:py-3 text-center">
                      <span className={`text-lg lg:text-xl font-bold tabular-nums ${i === 0 ? 'text-amber-600' : 'text-slate-500'}`}>
                        {user.avg_per_working_day?.toFixed(1)}
                        <span className="text-xs lg:text-sm font-semibold text-slate-400 ml-1">/day</span>
                      </span>
                    </td>
                    <td className="px-4 lg:px-6 py-2 lg:py-3">
                      <div className="flex items-center gap-2 lg:gap-3">
                        <div className="flex-1 h-2.5 lg:h-3 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full transition-all ${i === 0 ? 'bg-gradient-to-r from-amber-400 to-orange-500' : i < 3 ? 'bg-indigo-400' : 'bg-slate-300'}`} style={{ width: `${percentage}%` }} />
                        </div>
                        <span className="text-sm lg:text-base font-bold text-slate-400 w-12 text-right">{percentage.toFixed(0)}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  // ==================== FOOTER ====================
  const Footer = () => (
    <footer className="h-12 lg:h-14 border-t border-slate-200 bg-white px-4 lg:px-8 xl:px-12 flex items-center justify-between">
      <div className="flex items-center gap-2 lg:gap-3 text-slate-400">
        <Clock className="w-4 h-4 lg:w-5 lg:h-5" />
        <span className="text-sm lg:text-base font-medium">Last updated: {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}</span>
      </div>
      {isAutoRotate && (
        <div className="flex items-center gap-2 lg:gap-4 text-slate-400">
          <span className="text-sm lg:text-base font-medium">Next view in</span>
          <span className="px-3 lg:px-4 py-1.5 lg:py-2 bg-slate-100 rounded-lg lg:rounded-xl text-sm lg:text-base font-bold text-slate-700 tabular-nums">{formatTime(timeRemaining)}</span>
        </div>
      )}
    </footer>
  );

  // ==================== NOTIFICATION POPUP - FULL SCREEN ====================
  const NotificationOverlay = () => (
    notifications.length > 0 && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-md animate-fadeIn">
        <div className="relative mx-4">
          <div className="absolute -inset-4 lg:-inset-8 bg-gradient-to-r from-emerald-400 to-teal-400 rounded-[30px] lg:rounded-[60px] blur-2xl lg:blur-3xl opacity-70 animate-pulse" />
          
          <div className="relative bg-gradient-to-br from-emerald-500 via-teal-500 to-emerald-600 rounded-[24px] lg:rounded-[40px] px-8 lg:px-16 xl:px-20 py-10 lg:py-14 xl:py-16 shadow-2xl text-center min-w-[280px] lg:min-w-[500px] xl:min-w-[600px]">
            <div className="absolute -top-8 lg:-top-12 left-1/2 -translate-x-1/2">
              <div className="w-16 h-16 lg:w-24 lg:h-24 xl:w-28 xl:h-28 rounded-full bg-white shadow-2xl flex items-center justify-center animate-bounce">
                <Zap className="w-8 h-8 lg:w-12 lg:h-12 xl:w-16 xl:h-16 text-emerald-500" />
              </div>
            </div>
            
            <div className="absolute top-4 lg:top-6 left-4 lg:left-10"><Star className="w-6 h-6 lg:w-8 lg:h-8 xl:w-10 xl:h-10 text-yellow-300 animate-spin" style={{ animationDuration: '3s' }} /></div>
            <div className="absolute top-6 lg:top-10 right-4 lg:right-10"><Star className="w-5 h-5 lg:w-6 lg:h-6 xl:w-8 xl:h-8 text-yellow-300 animate-spin" style={{ animationDuration: '2s' }} /></div>
            <div className="absolute bottom-6 lg:bottom-10 left-6 lg:left-14"><Star className="w-4 h-4 lg:w-5 lg:h-5 xl:w-6 xl:h-6 text-yellow-300 animate-spin" style={{ animationDuration: '4s' }} /></div>
            <div className="absolute bottom-8 lg:bottom-14 right-6 lg:right-12"><Star className="w-5 h-5 lg:w-7 lg:h-7 xl:w-9 xl:h-9 text-yellow-300 animate-spin" style={{ animationDuration: '2.5s' }} /></div>
            
            <div className="pt-6 lg:pt-10 xl:pt-12">
              <p className="text-xl lg:text-2xl xl:text-3xl font-bold text-emerald-100 mb-2 lg:mb-3">{notifications[0]?.celebration}</p>
              <p className="text-3xl lg:text-5xl xl:text-6xl font-black text-white mb-3 lg:mb-4">{notifications[0]?.user_name}</p>
              <div className="inline-flex items-center gap-2 lg:gap-3 px-4 lg:px-6 xl:px-8 py-2 lg:py-3 xl:py-4 bg-white/20 rounded-full">
                <Award className="w-4 h-4 lg:w-5 lg:h-5 xl:w-6 xl:h-6 text-white" />
                <span className="text-base lg:text-xl xl:text-2xl font-bold text-white">{notifications[0]?.offer_name}</span>
              </div>
              <p className="text-base lg:text-lg xl:text-xl text-emerald-100 mt-4 lg:mt-6 xl:mt-8 font-semibold">Just closed a deal!</p>
            </div>
          </div>
        </div>
      </div>
    )
  );

  // ==================== MILESTONE CELEBRATION OVERLAY ====================
  const MilestoneOverlay = () => (
    milestoneAlert && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-lg animate-fadeIn">
        <div className="relative">
          {/* Confetti-like particles */}
          <div className="absolute -inset-20 overflow-hidden">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="absolute w-4 h-4 rounded-full animate-ping"
                style={{
                  background: COLORS[i % COLORS.length],
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 0.5}s`,
                  animationDuration: `${1 + Math.random()}s`
                }}
              />
            ))}
          </div>
          
          <div className="absolute -inset-12 bg-gradient-to-r from-amber-400 via-yellow-500 to-orange-500 rounded-full blur-3xl opacity-80 animate-pulse" />
          
          <div className="relative bg-gradient-to-br from-amber-500 via-yellow-500 to-orange-600 rounded-[50px] px-16 lg:px-24 py-14 lg:py-20 shadow-2xl text-center">
            <div className="absolute -top-10 left-1/2 -translate-x-1/2">
              <div className="w-20 h-20 lg:w-28 lg:h-28 rounded-full bg-white shadow-2xl flex items-center justify-center">
                <PartyPopper className="w-10 h-10 lg:w-14 lg:h-14 text-amber-500" />
              </div>
            </div>
            
            <div className="absolute top-8 left-8"><Sparkles className="w-10 h-10 text-white animate-bounce" /></div>
            <div className="absolute top-12 right-10"><Star className="w-8 h-8 text-yellow-200 animate-spin" style={{ animationDuration: '2s' }} /></div>
            <div className="absolute bottom-12 left-12"><Star className="w-6 h-6 text-white animate-spin" style={{ animationDuration: '3s' }} /></div>
            <div className="absolute bottom-8 right-8"><Sparkles className="w-8 h-8 text-yellow-200 animate-bounce" /></div>
            
            <div className="pt-8 lg:pt-12">
              <p className="text-2xl lg:text-3xl font-bold text-amber-100 mb-4">TEAM MILESTONE!</p>
              <p className="text-7xl lg:text-9xl font-black text-white mb-4 drop-shadow-lg">{milestoneAlert.milestone}</p>
              <p className="text-3xl lg:text-4xl font-black text-white mb-2">SALES</p>
              <div className="mt-6 inline-flex items-center gap-3 px-8 py-4 bg-white/20 rounded-full">
                <Trophy className="w-6 h-6 text-white" />
                <span className="text-xl lg:text-2xl font-bold text-white">INCREDIBLE TEAMWORK!</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  );

  // ==================== LEADER TAKEOVER OVERLAY ====================
  const LeaderTakeoverOverlay = () => (
    leaderTakeover && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-lg animate-fadeIn">
        <div className="relative">
          <div className="absolute -inset-16 bg-gradient-to-r from-rose-500 via-purple-500 to-indigo-600 rounded-full blur-3xl opacity-70 animate-pulse" />
          
          <div className="relative bg-gradient-to-br from-rose-500 via-purple-600 to-indigo-700 rounded-[40px] px-12 lg:px-20 py-12 lg:py-16 shadow-2xl text-center overflow-hidden">
            {/* Animated swords crossing */}
            <div className="absolute top-6 left-1/4 transform -rotate-45">
              <Swords className="w-12 h-12 lg:w-16 lg:h-16 text-white/30" />
            </div>
            <div className="absolute top-6 right-1/4 transform rotate-45">
              <Swords className="w-12 h-12 lg:w-16 lg:h-16 text-white/30" />
            </div>
            
            <div className="absolute -top-8 left-1/2 -translate-x-1/2">
              <div className="w-20 h-20 lg:w-24 lg:h-24 rounded-full bg-gradient-to-br from-amber-400 to-yellow-500 shadow-2xl flex items-center justify-center animate-bounce">
                <Crown className="w-10 h-10 lg:w-12 lg:h-12 text-white" />
              </div>
            </div>
            
            <div className="pt-10 lg:pt-14">
              <p className="text-xl lg:text-2xl font-bold text-purple-200 tracking-widest mb-2">NEW LEADER</p>
              <p className="text-lg lg:text-xl text-purple-300 mb-4">Takes the crown from {leaderTakeover.previousLeader}</p>
              
              <div className="bg-white/10 rounded-2xl px-10 py-6 mb-4">
                <p className="text-4xl lg:text-6xl font-black text-white mb-2">{leaderTakeover.name}</p>
                <div className="flex items-center justify-center gap-2">
                  <Flame className="w-6 h-6 text-orange-400 animate-pulse" />
                  <span className="text-2xl lg:text-3xl font-bold text-orange-300">{leaderTakeover.salesCount} Sales</span>
                  <Flame className="w-6 h-6 text-orange-400 animate-pulse" />
                </div>
              </div>
              
              <p className="text-xl lg:text-2xl font-bold text-white">THE RACE IS ON!</p>
            </div>
          </div>
        </div>
      </div>
    )
  );

  // ==================== COMEBACK ALERT BANNER ====================
  // DISABLED: Comeback alerts are now disabled - only show Sale & Broadcast popups
  const ComebackAlertBanner = () => null;

  // ==================== TV BROADCAST OVERLAY ====================
  const TvBroadcastOverlay = () => (
    tvBroadcast && (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 animate-fadeIn">
        <div className="relative max-w-4xl w-full mx-4">
          {/* Animated background glow */}
          <div className="absolute -inset-4 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 rounded-3xl blur-xl opacity-80 animate-pulse" />
          
          <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl overflow-hidden shadow-2xl border border-white/10">
            {/* Header stripe */}
            <div className="h-2 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
            
            <div className="p-8 lg:p-12">
              {/* Broadcast icon and label */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                  </svg>
                </div>
                <div>
                  <p className="text-indigo-400 text-sm font-semibold tracking-wider uppercase">ANNOUNCEMENT</p>
                  <p className="text-slate-400 text-xs">From {tvBroadcast.from}</p>
                </div>
                {tvBroadcast.priority === 'high' && (
                  <span className="ml-auto px-3 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded-full border border-red-500/30">
                    URGENT
                  </span>
                )}
              </div>
              
              {/* Title */}
              <h1 className="text-4xl lg:text-5xl font-black text-white mb-4 leading-tight">
                {tvBroadcast.title}
              </h1>
              
              {/* Message */}
              <p className="text-xl lg:text-2xl text-slate-300 leading-relaxed">
                {tvBroadcast.message}
              </p>
              
              {/* Auto-close indicator */}
              <div className="mt-8 flex items-center gap-2 text-slate-500 text-sm">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span>This message will auto-close in 60 seconds</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  );

  // ==================== ACHIEVEMENT BADGE COMPONENT ====================
  const AgentBadges = ({ agentName }) => {
    const badges = agentBadges[agentName] || [];
    if (badges.length === 0) return null;
    
    return (
      <div className="flex items-center gap-1 mt-0.5">
        {badges.slice(0, 3).map(badgeKey => {
          const badge = ACHIEVEMENT_BADGES[badgeKey];
          return (
            <span 
              key={badgeKey} 
              className="text-xs lg:text-sm" 
              title={`${badge.label}: ${badge.desc}`}
            >
              {badge.icon}
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-slate-50 flex flex-col overflow-hidden">
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: scale(0.8); } to { opacity: 1; transform: scale(1); } }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }
      `}</style>
      
      <Header />
      
      {/* Gamification Overlays - Priority: Broadcast > Milestone > LeaderTakeover > Notification */}
      <TvBroadcastOverlay />
      <MilestoneOverlay />
      <LeaderTakeoverOverlay />
      <NotificationOverlay />
      
      {currentView === 'daily' ? <DailySalesView /> : <LeaderboardView />}
      <Footer />
    </div>
  );
};

export default TVDisplayPage;
